import type { JSX, ReactNode } from "react";
import { useId } from "react";
import { useI18n } from "../i18n/react.js";
import { SpiderLoader } from "./SpiderLoader.js";
import type { MessageKey } from "../i18n/index.js";

/**
 * Arayüz primitifleri — Rapor 14 §6, Rapor 15 F1-29.
 *
 * ### ⚠️ Bileşen kütüphanesi YOK
 * MUI/Chakra/Radix kritik yola 40–120 KB ekler. Bu ürünün ihtiyacı beş
 * primitiftir; her biri ~30 satırdır ve **erişilebilirlik davranışını**
 * (odak, `aria-*`, canlı bölge) tek yerde toplar.
 *
 * ### ⚠️ Sabit renk/ölçü YASAK
 * Hiçbir bileşen `#hex`, `px` renk veya sabit yükseklik yazmaz. Görsel
 * kararlar `data-*` özniteliğine düşer, CSS `tokens.css`/`components.css`
 * içinde token'lardan okur. Kiracı markası böylece **tek noktadan** ezilir.
 */

// ═══════════════════════════════════════════════════════════════════════
// Button
// ═══════════════════════════════════════════════════════════════════════

export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "md" | "sm";
  disabled?: boolean;
  /** İşlem sürüyor: düğme kilitlenir ve `aria-busy` duyurulur. */
  busy?: boolean;
  /** İki durumlu düğme (mikrofon, kamera, el). */
  pressed?: boolean;
  expanded?: boolean;
  controls?: string;
  /** Yalnız ikon gösterildiğinde **zorunlu** erişilebilir ad. */
  label?: string;
  /**
   * ⭐ Fare ipucu — **neden pasif** olduğunu söyleyen tek kanal.
   *
   * 🚨 Bu alan arayüzde ZATEN kullanılıyordu (`room.forcedMuteMic`,
   *    `room.noPublishCap`) ama `ButtonProps` içinde tanımlı değildi ve
   *    `<button>`'a hiç geçirilmiyordu. JSX'te nesne yayılımı (`{...obj}`)
   *    fazladan alan denetimini atladığı için TypeScript de susuyordu.
   *    Sonuç: moderatör tarafından susturulan kullanıcı gri bir düğme
   *    görüyor, sebebini **hiçbir yerde** öğrenemiyordu — kodun kendi yorumu
   *    "sebep `title` ile açıkça söylenir" derken.
   *
   * ⚠️ `title` erişilebilir ad YERİNE geçmez (ekran okuyucular tutarsız okur);
   *    ikon düğmelerde `label` ile BİRLİKTE verilir.
   */
  title?: string;
  autoFocus?: boolean;
}

export function Button(props: ButtonProps): JSX.Element {
  const disabled = props.disabled === true || props.busy === true;
  return (
    <button
      // ⚠️ `type` varsayılanı `button`: form içindeki etiketsiz bir düğme
      //    tarayıcıda `submit` sayılır ve formu kazara gönderir.
      type={props.type ?? "button"}
      data-variant={props.variant ?? "secondary"}
      data-size={props.size ?? "md"}
      disabled={disabled}
      onClick={props.onClick}
      aria-busy={props.busy === true ? true : undefined}
      aria-pressed={props.pressed}
      aria-expanded={props.expanded}
      aria-controls={props.controls}
      aria-label={props.label}
      title={props.title}
      autoFocus={props.autoFocus}
    >
      {props.children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Field
// ═══════════════════════════════════════════════════════════════════════

export interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  name?: string;
  type?: "text" | "tel" | "password";
  inputMode?: "text" | "numeric";
  pattern?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  hint?: string;
  /** Doluysa alan `aria-invalid` olur ve hata metni açıklamaya bağlanır. */
  error?: string;
  disabled?: boolean;
}

/**
 * ⚠️ Hata metni **`aria-describedby` ile bağlanır**. Alanın yanında kırmızı
 *    bir metin göstermek ekran okuyucu için hiçbir şey ifade etmez; kullanıcı
 *    alana odaklandığında hatayı DUYMALIDIR.
 */
export function Field(props: FieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const described = [props.hint ? hintId : null, props.error ? errorId : null]
    .filter((v): v is string => v !== null)
    .join(" ");

  return (
    <div data-field>
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id}
        name={props.name}
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        inputMode={props.inputMode}
        pattern={props.pattern}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        required={props.required}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        disabled={props.disabled}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={described === "" ? undefined : described}
      />
      {props.hint ? (
        <small id={hintId} data-hint>
          {props.hint}
        </small>
      ) : null}
      {props.error ? (
        <small id={errorId} data-level="error">
          {props.error}
        </small>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Banner
// ═══════════════════════════════════════════════════════════════════════

export type BannerLevel = "info" | "warn" | "error";

/**
 * ⚠️ `role` **seviyeye göre** seçilir:
 *    - `error` → `alert`: ekran okuyucu okuduğunu **keser** ve hemen duyurur.
 *    - diğerleri → `status`: sıradaki uygun anda duyurulur.
 *    Her şeyi `alert` yapmak, kullanıcıyı sürekli bölen bir arayüz üretir.
 */
export function Banner(props: {
  level: BannerLevel;
  children: ReactNode;
  action?: { label: string; onAct: () => void };
}): JSX.Element {
  return (
    <p role={props.level === "error" ? "alert" : "status"} data-level={props.level} data-banner>
      <span>{props.children}</span>
      {props.action ? (
        <Button size="sm" variant="ghost" onClick={props.action.onAct}>
          {props.action.label}
        </Button>
      ) : null}
    </p>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Spinner · VisuallyHidden
// ═══════════════════════════════════════════════════════════════════════

/**
 * ⭐ **Tek yükleme göstergesi — marka örümceği, gövdeden bacaklara akan neon.**
 *
 * 🚨 Burada jenerik bir CSS dönen dairesi vardı. Bileşenin **API'si
 *    değişmedi**; bu yüzden yedi çağrı yerinin hepsi (yönetim konsolu, katılım
 *    formu, kullanım sayfası, lobi, sohbet) tek değişiklikle yeni göstergeye
 *    geçer. Çağrı yerlerini tek tek düzenlemek, birinin unutulması ve ürünün
 *    iki farklı bekleme yüzüyle yayınlanması demekti.
 *
 * ⚠️ Grafik `aria-hidden`dır; görünmez metin ZORUNLUDUR. Akan bir ışık ekran
 *    okuyucuya hiçbir şey söylemez.
 *
 * ⚠️ `aria-live` YOKTUR: yükleme durumu kendiliğinden değişir ve her
 *    değişimde duyurulması gürültü olur.
 */
export function Spinner(props: { labelKey?: MessageKey }): JSX.Element {
  const i18n = useI18n();
  return (
    <span role="status" data-spinner>
      <SpiderLoader />
      <VisuallyHidden>{i18n.t(props.labelKey ?? "common.loading")}</VisuallyHidden>
    </span>
  );
}

export function VisuallyHidden(props: { children: ReactNode }): JSX.Element {
  return <span data-visually-hidden>{props.children}</span>;
}

/**
 * Kibar canlı bölge — kalıcı olmayan duyurular (ör. "el kaldırdı").
 *
 * ⚠️ İçerik **değiştiğinde** okunur; bu yüzden aynı metni iki kez yazmak
 *    ikinci kez duyurulmaz. Çağıran taraf gerekiyorsa metne sayaç ekler.
 */
export function LiveRegion(props: { message: string }): JSX.Element {
  return (
    <div aria-live="polite" aria-atomic="true" data-visually-hidden>
      {props.message}
    </div>
  );
}
